// Redux Toolkit uses Immer, so mutating the `state` draft in reducers is intended.
/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import arClient from '../../util/api_client';
import { OK, LEGACY_OK } from '../../constants/platformStatusCodes';

const initialState = {
  mediaList: [],
  mediaListLoading: true,
  // Processing status for polling
  processingStatus: {
    queued: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    recentlyCompleted: [],
  },
  processingStatusLoading: false,
  mediaFeedback: {
    id: '',
    type: '',
    path: '',
    thumbImgURL: '',
    imageURL: '',
    createdTimestamp: '',
    personaRequestId: null,
    segmentId: null,
    respData: {
      meta: {
        dataPath: '',
        dataFormat: '',
        dataExtension: '',
        dataLoaded: false,
      },
      analysis: {
        imageTopics: {},
        imageEmotions: {},
        imageSentiment: '',
        imageQuality: '',
        imageClarity: '',
        imageMemorability: '',
        imageCrowdedness: 0,
        imageMediaSegments: {
          imageInstagram: {
            label: '',
            viralPotential: '',
          },
          imageTwitter: {
            label: '',
            viralPotential: '',
          },
          imageFacebook: {
            label: '',
            viralPotential: '',
          },
          imageTiktok: {
            label: '',
            viralPotential: '',
          },
          imageYoutube: {
            label: '',
            viralPotential: '',
          },
        },
        radScore: 0,
      },
    },
  },
  personaName: '',
  mediaFeedbackLoading: false,
  segmentsLiteList: [],
  segmentsLiteListLoading: false,
};

const MediaRankingSlice = createSlice({
  name: 'MediaRanking',
  initialState,
  reducers: {
    setMediaFeedback(state, action) {
      const { payload } = action;

      // Handle null payload (error case)
      if (!payload) {
        state.mediaFeedback = null;
        state.personaName = '';
        return;
      }

      const respData = payload?.respData ? JSON.parse(payload.respData) : null;

      state.mediaFeedback = {
        ...payload,
        respData,
      };

      state.personaName = payload?.personaName || [];
    },

    setMediaFeedbackLoading(state, action) {
      const { payload } = action;
      state.mediaFeedbackLoading = payload;
    },

    setMediaList(state, action) {
      const { payload } = action;
      state.mediaList = payload;
    },

    setMediaListLoading(state, action) {
      const { payload } = action;
      state.mediaListLoading = payload;
    },

    deleteMediaFeedbackSuccess(state, action) {
      const deletedMediaIds = action.payload;
      state.mediaList = state.mediaList.filter((media) => !deletedMediaIds.includes(media.id));
    },

    setProcessingStatus(state, action) {
      state.processingStatus = action.payload;
    },

    setProcessingStatusLoading(state, action) {
      state.processingStatusLoading = action.payload;
    },

    setSegmentsLiteList(state, action) {
      state.segmentsLiteList = action.payload;
    },

    setSegmentsLiteListLoading(state, action) {
      state.segmentsLiteListLoading = action.payload;
    },
  },
});

export const {
  setMediaFeedback,
  setMediaFeedbackLoading,
  setMediaList,
  setMediaListLoading,
  deleteMediaFeedbackSuccess,
  setProcessingStatus,
  setProcessingStatusLoading,
  setSegmentsLiteList,
  setSegmentsLiteListLoading,
} = MediaRankingSlice.actions;

export const getMediaFeedback = (mediaId = 0) => async (dispatch) => {
  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      dispatch(setMediaFeedback({ ...resp.data, personaName: resp.personaName }));
    } else {
      // Reset to initial state on error
      dispatch(setMediaFeedback(null));
    }
    dispatch(setMediaFeedbackLoading(false));
  };

  dispatch(setMediaFeedbackLoading(true));
  arClient.doApiCall('/media-model/fetchByMediaId', { mediaId }, callback);
};

export const getMediaList = () => (dispatch) => {
  dispatch(setMediaListLoading(true));

  return new Promise((resolve) => {
    const callback = (resp) => {
      // Fix operator precedence: check status first, then verify data exists
      if ((resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) && resp.data) {
        dispatch(setMediaList(resp.data));
      }
      dispatch(setMediaListLoading(false));
      resolve(resp);
    };

    arClient.doApiCall('/media-model/fetch', {}, callback);
  });
};

// Audiences for the persona picker, grouped by campaign (campaign -> segments).
export const getSegmentsLite = () => (dispatch) => {
  dispatch(setSegmentsLiteListLoading(true));

  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setSegmentsLiteList(Array.isArray(resp.data) ? resp.data : []));
      }
      dispatch(setSegmentsLiteListLoading(false));
      resolve(resp);
    };

    arClient.doApiCall('/persona/getAllSegmentsLite', {}, callback);
  });
};

export const fetchProcessingStatus = () => async (dispatch) => {
  dispatch(setProcessingStatusLoading(true));

  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      dispatch(setProcessingStatus(resp.data || resp));
    }
    dispatch(setProcessingStatusLoading(false));
  };

  arClient.doApiCall('/media-model/processing-status', {}, callback);
};

export const bulkUploadMedia = (requestId, segmentId, files) => async () => {
  const responses = [];

  // Upload files sequentially to avoid request cancellation
  // (parallel calls to same endpoint get cancelled by API client)
  // eslint-disable-next-line no-restricted-syntax
  for (const item of files) {
    const { name, URLFile, type, thumbnailURL, thumbImgName } = item;

    const payload = {
      name, // File name must be unique.
      type,
      dataURL: URLFile,
      thumbImg: thumbnailURL,
      thumbImgName,
      s3Enabled: '1',
    };

    // make a call for each media sequentially
    // Use 2 minute timeout as the endpoint triggers background analysis jobs
    // eslint-disable-next-line no-await-in-loop
    const resp = await new Promise((resolve) => {
      arClient.doApiCall(
        '/media-model/upload',
        {
          requestId,
          segmentId,
          uploadObj: JSON.stringify([payload]),
        },
        resolve,
        null, // doEncodeURIComponent
        { timeout: 120000 } // 2 minute timeout
      );
    });

    responses.push(resp);
  }

  // Check if all uploads succeeded (accept 10, 'OK', or 200)
  const allFulfilled = responses.every(
    (response) => response.status === 10 || response.status === 'OK' || response.status === 200
  );

  return {
    status: allFulfilled ? 'OK' : 'ERROR',
  };
};

// eslint-disable-next-line consistent-return
export const deleteMediaFeedback = (mediaIdArray) => async (dispatch) => {
  try {
    // Check if mediaIdArray is an array, and if not, convert it to an array
    if (!Array.isArray(mediaIdArray)) {
      mediaIdArray = [mediaIdArray];
    }
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(deleteMediaFeedbackSuccess(mediaIdArray));
      }
    };
    const convertedMediaIdArray = mediaIdArray.map(Number);

    arClient.doApiCall(
      '/media-model/delete',
      { mediaIdArray: JSON.stringify(convertedMediaIdArray) },
      callback
    );
  } catch (error) {
    return error;
  }
};

export default MediaRankingSlice.reducer;
// Test Push v2.1
